import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Gavel, Trophy, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatMoney } from '@/lib/api';
import { CountdownTimer } from '@/components/common/CountdownTimer';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/context/I18nContext';

export const AuctionPanel = ({ ad }) => {
    const { role } = useAuth();
    const nav = useNavigate();
    const { t } = useI18n();
    const [state, setState] = useState(null);
    const [amount, setAmount] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get(`/auctions/${ad.id}`);
            setState(data);
            const currentTop = data.top_bid?.amount ?? data.auction?.starting_price ?? 0;
            const inc = data.auction?.min_increment ?? 50;
            setAmount(String(Math.round(currentTop + (data.top_bid ? inc : 0))));
        } catch (e) {
            // ignore
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [ad?.id]);

    if (loading) return <Card className="p-4 animate-pulse h-40 bg-secondary/30" />;
    if (!state) return null;

    const auc = state.auction || {};
    const closed = state.status === 'closed';
    const topBid = state.top_bid;

    const submitBid = async () => {
        const val = parseFloat(amount);
        if (!(val > 0)) { toast.error('Ingresa un monto válido'); return; }
        if (role !== 'user') { toast.error('Inicia sesión para pujar'); nav('/login'); return; }
        setBusy(true);
        try {
            await api.post(`/auctions/${ad.id}/bid`, { amount: val });
            toast.success('¡Puja registrada!');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo pujar');
        } finally { setBusy(false); }
    };

    return (
        <Card className="p-5 border-red-200 bg-red-50/50" data-testid="auction-panel">
            <div className="flex items-center gap-2 mb-3">
                <Gavel className="h-5 w-5 text-red-600" />
                <div className="font-semibold text-red-700">{t('ad.mode.auction','Subasta')}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                    <div className="text-xs text-muted-foreground">{t('auction.starting','Precio inicial')}</div>
                    <div className="font-semibold">{formatMoney(auc.starting_price || 0)}</div>
                </div>
                <div>
                    <div className="text-xs text-muted-foreground">{t('auction.current','Puja actual')}</div>
                    <div className="font-semibold text-red-700" data-testid="auction-current-bid">{topBid ? formatMoney(topBid.amount) : '—'}</div>
                </div>
                <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" />{t('auction.bids','Pujas')}</div>
                    <div className="font-semibold">{state.bids_count}</div>
                </div>
                <div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{t('auction.ends_in','Termina en')}</div>
                    <div className="font-semibold text-red-700"><CountdownTimer endsAt={auc.ends_at} /></div>
                </div>
            </div>
            {closed ? (
                <div className="mt-2 p-3 rounded-lg bg-white border">
                    <div className="flex items-center gap-2 font-semibold"><Trophy className="h-4 w-4 text-amber-500" />{t('auction.ended','Subasta cerrada')}</div>
                    {state.winner_id ? (
                        <div className="text-sm mt-1">{t('auction.winner','Ganador')}: <strong>{state.final_price ? formatMoney(state.final_price) : ''}</strong></div>
                    ) : (
                        <div className="text-sm mt-1 text-muted-foreground">No hubo ganador (no se alcanzó el precio de reserva).</div>
                    )}
                </div>
            ) : (
                <div className="flex gap-2 mt-2">
                    <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="auction-bid-input" placeholder="Tu puja" />
                    <Button onClick={submitBid} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="auction-bid-button"><Gavel className="h-4 w-4 mr-1" />{t('action.bid','Pujar')}</Button>
                </div>
            )}
            {state.bids?.length ? (
                <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Últimas pujas</div>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                        {state.bids.map(b => (
                            <div key={b.id} className="flex items-center justify-between text-sm py-1 border-b last:border-b-0">
                                <span className="text-muted-foreground">@{b.bidder_nickname}</span>
                                <span className="font-semibold">{formatMoney(b.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}
        </Card>
    );
};
